/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */
/* eslint-disable no-restricted-syntax */

import { HTTPSnippet } from '@readme/httpsnippet';
import websocketNodeSnippet from './templates/websocket-node.hbs';

export function getGeneratedOptionsForOperation(config, operation, pathName, method, codeLanguages, maxDepth, api, apiHandler) {
  let contenttype = apiHandler.getDefaultContentTypeHeader(operation, api);
  if (config.contenttype) {
    contenttype = config.contenttype;
  }

  let allParameters = apiHandler.getParameters(operation, pathName, contenttype, api);
  allParameters = apiHandler.removeReservedParameters(allParameters);
  config.parametersArray = [];
  Object.keys(allParameters).forEach((parameterName) => {
    config.parametersArray.push(allParameters[parameterName]);
  });

  const security = operation.security || api.security;
  const securityDefinitions = apiHandler.getSecurityDefinitions();

  if (security && securityDefinitions) {
    const selectedSecurity = security[config.selectedSecurityOption];
    const securityConfig = apiHandler.getSecurityFlowConfig(selectedSecurity, securityDefinitions, null, config.endpoint);

    config = Object.assign(config, securityConfig);
  }

  if (operation['x-ibm-soap'] && operation['x-ibm-soap']['soap-action']) {
    config.soapAction = operation['x-ibm-soap']['soap-action'];
  }

  return {
    api,
    operation,
    path: pathName,
    verb: method,
    config,
    clientId: 'clientId',
    clientSecret: 'clientSecret',
    languages: codeLanguages,
    endpoint: config.endpoint,
    maxDepth,
    apiHandler
  };
}

/**
 * Generate the code snippets
 * @param api
 * @param path
 * @param verb
 * @param operation
 * @param config
 * @param clientId
 * @param clientSecret
 * @param codeLanguages
 * @param references
 * @param values an object containing key=value property pairs that provide values for parameters used instead of place holder REPLACE_THIS_KEY text
 * @returns {{}}
 */
function generateCodeSnippets(api, path, verb, operation, config, clientId, clientSecret, codeLanguages, references, endpoint, values, apiHandler) {
  if (!clientId) clientId = 'REPLACE_THIS_KEY';
  if (!clientSecret) clientSecret = 'REPLACE_THIS_KEY';

  if (!endpoint) {
    if (path && verb && api.paths && api.paths[path] && api.paths[path][verb] && api.paths[path][verb].servers) {
      endpoint = api.paths[path][verb].servers[0].url;
    } else if (path && api.paths && api.paths[path] && api.paths[path].servers) {
      endpoint = api.paths[path].servers[0].url;
    } else if (api.endpoint) {
      endpoint = api.endpoint;
    } else if (
      api['x-ibm-configuration'] &&
      api['x-ibm-configuration'].servers &&
      api['x-ibm-configuration'].servers[0] &&
      api['x-ibm-configuration'].servers[0].url
    ) {
      endpoint = api['x-ibm-configuration'].servers[0].url;
    } else if (api['x-ibm-endpoints'] && api['x-ibm-endpoints'][0] && api['x-ibm-endpoints'][0].endpointUrl) {
      endpoint = api['x-ibm-endpoints'][0].endpointUrl;
      endpoint += api.basePath ? api.basePath : '';
    } else if (api.openapi && api.servers && api.servers[0] && api.servers[0].url) {
      endpoint = api.servers[0].url;
    } else {
      // derive it from the api
      endpoint = `://${api.host}`;
      if (api.schemes) {
        endpoint = api.schemes[0] + endpoint;
      } else {
        endpoint = `https${endpoint}`;
      }
      endpoint += api.basePath ? api.basePath : '';
    }
  }

  // For OAI3 the endpoint is passed in as an object containing a URL.  It looks
  // like it might contain multiple, so just take the last one and make it a
  // string like the code expects
  if (typeof endpoint === 'object') {
    let ep;
    // eslint-disable-next-line guard-for-in
    for (const url in endpoint) {
      ep = endpoint[url];
    }
    endpoint = ep;
  }

  endpoint = endpoint?.replace(/\/$/, '');
  const headersarray = [];
  const queryarray = [];
  let body = null;
  let protocols;
  if (!values) {
    values = {};
  }

  if (config && config.requiresClientId) {
    if (config.clientIdLocation === 'query') {
      if (config.clientIdName) {
        queryarray.push({ name: config.clientIdName, value: clientId });
      } else {
        queryarray.push({ name: 'client_id', value: clientId });
      }
    } else if (config.clientIdLocation === 'header') {
      if (config.clientIdName) {
        headersarray.push({ name: config.clientIdName, value: clientId });
      } else {
        headersarray.push({ name: 'X-IBM-Client-Id', value: clientId });
      }
    }
  }
  if (config && config.requiresClientSecret) {
    if (config.clientSecretLocation === 'query') {
      if (config.clientSecretName) {
        queryarray.push({ name: config.clientSecretName, value: clientSecret });
      } else {
        queryarray.push({ name: 'client_secret', value: clientSecret });
      }
    } else if (config.clientSecretLocation === 'header') {
      if (config.clientSecretName) {
        headersarray.push({ name: config.clientSecretName, value: clientSecret });
      } else {
        headersarray.push({ name: 'X-IBM-Client-Secret', value: clientSecret });
      }
    }
  }
  if (config && config.requiresBasicAuth) {
    if (values.basicAuth) {
      headersarray.push({ name: 'Authorization', value: `Basic ${values.basicAuth}` });
    } else {
      headersarray.push({ name: 'Authorization', value: 'Basic REPLACE_BASIC_AUTH' });
    }
  }
  if (config && config.requiresOauth) {
    if (values.oauthToken) {
      headersarray.push({ name: 'Authorization', value: `Bearer ${values.oauthToken}` });
    } else {
      headersarray.push({ name: 'Authorization', value: 'Bearer REPLACE_BEARER_TOKEN' });
    }
  }
  if (config && config.requiresHttpBearerToken) {
    if (config.httpBearerToken) {
      headersarray.push({ name: 'Authorization', value: `Bearer ${config.httpBearerToken}` });
    } else {
      headersarray.push({ name: 'Authorization', value: 'Bearer REPLACE_BEARER_TOKEN' });
    }
  }
  if (config && config.requiresDigestAuth) {
    if (config.httpDigestToken) {
      headersarray.push({ name: 'Authorization', value: `Digest ${config.httpDigestToken}` });
    } else {
      headersarray.push({
        name: 'Authorization',
        value: 'Digest username="USERNAME_VALUE", realm="REALM_URL", nonce="NONCE_VALUE", opaque="OPAQUE_VALUE", response="HASHED_CREDS"'
      });
    }
  }
  if (config && config.requiresHobaAuth) {
    if (config.httpHobaToken) {
      headersarray.push({ name: 'Authorization', value: `HOBA ${config.httpHobaToken}` });
    } else {
      headersarray.push({ name: 'Authorization', value: 'HOBA result="SIGNATURE_VALUE"' });
    }
  }
  if (config && config.requiresScramShaAuth) {
    if (config.httpScramShaToken) {
      headersarray.push({ name: 'Authorization', value: `SCRAM-SHA-256 ${config.httpScramShaToken}` });
    } else {
      headersarray.push({ name: 'Authorization', value: 'SCRAM-SHA-256 sid="SID_VALUE", data="ENCRYPTED_CREDS"' });
    }
  }
  if (config && config.requiresVapidAuth) {
    if (config.httpVapidToken) {
      headersarray.push({ name: 'Authorization', value: `vapid ${config.httpVapidToken}` });
    } else {
      headersarray.push({ name: 'Authorization', value: 'vapid t="JSON_WEB_TOKEN", k="JSON_WEB_KEY"' });
    }
  }
  if (config && config.requiresNegotiateAuth) {
    if (config.httpNegotiateToken) {
      headersarray.push({ name: 'Authorization', value: `Negotiate ${config.httpNegotiateToken}` });
    } else {
      headersarray.push({ name: 'Authorization', value: 'Negotiate "GSSAPI_MESSAGE_TEXT"' });
    }
  }
  if (config && config.requiresOAuthAuth) {
    if (config.httpOAuthToken) {
      headersarray.push({ name: 'Authorization', value: `OAuth ${config.httpOAuthToken}` });
    } else {
      headersarray.push({
        name: 'Authorization',
        value:
          'OAuth realm="REALM_URL", oauth_consumer_key="ACCESS_KEY", oauth_token="TOKEN_STRING", oauth_nonce="UNIQUELY GENERATED RANDOM STRING", oauth_signature="SIGNATURE", oauth_signature_method="HMAC-SHA256", oauth_timestamp="EPOCH_SECONDS", oauth_version="1.0"'
      });
    }
  }
  if (config && config.requiresMutualAuth) {
    if (config.httpMutualToken) {
      headersarray.push({ name: 'Authorization', value: `Mutual ${config.httpMutualToken}` });
    } else {
      headersarray.push({
        name: 'Authorization',
        value: 'Mutual version="VERSION_TOKEN", sid="SESSION_ID", nc="NONCE_VALUE", vkc="AUTH_VERIFICATION_VALUE"'
      });
    }
  }

  let contenttype = apiHandler.getDefaultContentTypeHeader(operation, api);
  let accept = apiHandler.getDefaultAcceptHeader(operation, api);

  // allow for overrides
  accept = config.accept ? config.accept : accept;
  contenttype = config.contenttype ? config.contenttype : contenttype;

  const serverVariables = apiHandler.getServerVariables(api, endpoint, { path, method: verb });
  if (serverVariables) {
    Object.keys(serverVariables).forEach((key) => {
      endpoint = endpoint.replace(`{${key}}`, `REPLACE_${key.toUpperCase()}_VARIABLE`);
    });
  }
  const dereferencedParameters = apiHandler.getParameters(operation, path, contenttype, api);
  let parameters = config.parametersArray;
  if (!parameters) parameters = [];
  // path parameters
  const pathParameters = parameters.filter((parameter) => parameter.in === 'path');
  if (pathParameters.length > 0) {
    pathParameters.forEach((parameter) => {
      let value = `REPLACE_${parameter.name.toUpperCase()}`;
      if (values[parameter.name]) {
        value = values[parameter.name];
      }
      if (parameter.default) {
        value = parameter.default.toString();
      } else if (parameter['x-example']) {
        value = parameter['x-example'].toString();
      }
      path = path.replace(`{${parameter.name}}`, value);
    });
  }
  // query parameters
  const objectQueryParameters = {};
  const queryParameters = parameters.filter((parameter) => parameter.in === 'query');
  if (queryParameters.length > 0) {
    queryParameters.forEach((parameter) => {
      let type;
      let value = 'REPLACE_THIS_VALUE';
      if (values[parameter.name]) {
        value = values[parameter.name];
      }
      if (parameter.default) {
        value = parameter.default.toString();
      } else if (parameter['x-example']) {
        value = parameter['x-example'].toString();
      }
      if (dereferencedParameters[parameter.name]) {
        type = dereferencedParameters[parameter.name].type ?? dereferencedParameters[parameter.name]?.schema?.type;
      }
      if (type === 'object' || type === 'array') {
        value = `REPLACE_${parameter.name.toUpperCase()}`;
        objectQueryParameters[parameter.name] = value;
      }
      queryarray.push({ name: parameter.name, value });
    });
  }
  // handle external security
  if (config.externalApiKeys && Object.keys(config.externalApiKeys).length !== 0) {
    // eslint-disable-next-line guard-for-in
    for (const prop in config.externalApiKeys) {
      const key = config.externalApiKeys[prop];
      let value = 'REPLACE_THIS_VALUE';
      if (values[key.name]) {
        value = values[key.name];
      }
      if (key.in === 'query') {
        queryarray.push({ name: key.name, value });
      } else if (key.in === 'header') {
        headersarray.push({ name: key.name, value });
      }
    }
  }
  let url = endpoint + path;

  // is this SOAP? if so, ditch the operationId from the end...
  if (config.soapAction) {
    const suffix = `/${operation.operationId}`;
    if (url.indexOf(suffix, url.length - suffix.length) !== -1) {
      url = url.substring(0, url.length - suffix.length);
    }
  }
  if (config.soapAction) {
    headersarray.push({ name: 'SOAPAction', value: config.soapAction });
  }

  // headers

  const headerParameters = parameters.filter((parameter) => parameter.in === 'header');
  if (headerParameters.length > 0) {
    headerParameters.forEach((parameter) => {
      let value = 'REPLACE_THIS_VALUE';
      if (values[parameter.name]) {
        value = values[parameter.name];
      }
      if (parameter.default) {
        value = parameter.default.toString();
      } else if (parameter['x-example']) {
        value = parameter['x-example'].toString();
      }
      headersarray.push({ name: parameter.name, value });
      if (parameter.name.toLowerCase() === 'sec-websocket-protocol') {
        protocols = 'REPLACE_SEC-WEBSOCKET-PROTOCOL';
      }
    });
  }

  const cookieParameters = parameters.filter((parameter) => parameter.in === 'cookie');
  const cookies = [];
  if (cookieParameters.length > 0) {
    cookieParameters.forEach((parameter) => {
      let value = 'REPLACE_THIS_VALUE';
      if (values[parameter.name]) {
        value = values[parameter.name];
      }
      if (parameter.default) {
        value = parameter.default.toString();
      } else if (parameter['x-example']) {
        value = parameter['x-example'].toString();
      }
      cookies.push(`${parameter.name}=${value}`);
    });
    headersarray.push({ name: 'Cookie', value: cookies.join(';') });
  }
  const allParameters = apiHandler.getParameters(operation, path, contenttype, api);
  const bodyParameters = Object.values(allParameters).filter((parameterObject) => parameterObject.in === 'body');
  const formDataParameters = Object.values(allParameters).filter((parameterObject) => parameterObject.in === 'formData');

  const formDataArray = [];
  const formDataParams = [];
  if (bodyParameters.length > 0) {
    body = 'REPLACE_BODY';
    // we have a body parameter - take from values or generate a sample
    if (values[bodyParameters[0].name]) {
      body = values[bodyParameters[0].name];
    }
  } else if (formDataParameters.length > 0) {
    formDataParameters.forEach((parameter) => {
      let value = 'REPLACE_THIS_VALUE';
      if (values[parameter.name]) {
        value = values[parameter.name];
      }
      formDataArray.push(`${parameter.name}: ${value}`);
      formDataParams.push({ name: parameter.name, value });
    });
    body = formDataArray.join('\n');
  }
  // insert contenttype and accept headers as well
  if (contenttype && body) {
    // only add content type if we're sending content
    headersarray.push({ name: 'content-type', value: contenttype });
  }
  if (accept) {
    headersarray.push({ name: 'accept', value: accept });
  }

  // create the HAR request configuration
  const confHAR = {
    method: verb.toUpperCase(),
    url,
    headers: headersarray,
    queryString: queryarray
  };

  if (body) {
    confHAR.postData = {
      mimeType: contenttype,
      text: body
    };
    if (formDataParameters.length > 0) {
      confHAR.postData.params = formDataParams;
    }
  }
  // We must pre-process the URL to replace any $(var) inserts that may be contained in the
  // host part of the URL as the URL parser will think they are part of the path rather than
  // part of the host.
  const subLookup = {};
  let increment = 0;
  if (api.host) {
    const matches = api.host.match(/(\$\([^)]*\))/g);
    if (matches) {
      let newURL = confHAR.url;
      // switch each match with a generated hostname-safe value
      const len = matches.length;
      for (let i = 0; i < len; i += 1) {
        increment += 1;
        const fakeHost = `azazaz090909-${increment}`;
        subLookup[fakeHost] = matches[i];
        newURL = newURL.replace(matches[i], fakeHost);
      } // end for
      confHAR.url = newURL;
    }
  }

  // use the lookup table to switch the fake values with real ones in the actual output
  const restoreURL = function (snippet) {
    let ret = snippet;
    if (increment > 0) {
      // eslint-disable-next-line guard-for-in
      for (const key in subLookup) {
        const replacement = subLookup[key];
        ret = ret.replace(key, replacement);
      } // end for
    }
    return ret;
  };

  let content;
  if (/(wss?):\/\//.test(confHAR.url)) {
    confHAR.protocols = protocols;
    content = { nodeContent: websocketNodeSnippet(confHAR) };
  } else {
    const mySnippet = new HTTPSnippet(confHAR);

    if (!codeLanguages) codeLanguages = ['node', 'ruby', 'python', 'php', 'java', 'go', 'swift', 'c', 'csharp'];
    content = {};
    codeLanguages.forEach((codeLanguage) => {
      if (codeLanguage === 'curl') {
        content.curlContent = restoreURL(mySnippet.convert('shell', 'curl'));
      } else if (codeLanguage === 'httpie') {
        content.curlContent = restoreURL(mySnippet.convert('shell', 'httpie'));
      } else if (codeLanguage === 'wget') {
        content.curlContent = restoreURL(mySnippet.convert('shell', 'wget'));
      } else if (codeLanguage === 'java') {
        content.javaContent = restoreURL(mySnippet.convert('java', 'okhttp'));
      } else if (codeLanguage === 'node') {
        content.nodeContent = restoreURL(mySnippet.convert('node', 'request'));
      } else {
        content[`${codeLanguage}Content`] = restoreURL(mySnippet.convert(codeLanguage));
      }
    });
  }

  Object.entries(objectQueryParameters).forEach(([parameterName, parameterValue]) => {
    Object.entries(content).forEach(([key]) => {
      content[key] = content[key].replace(`${parameterName}=${parameterValue}`, parameterValue);
    });
  });

  return content;
}

export function generateOperationContent(options) {
  // we're generating operation content
  const snippets = generateCodeSnippets(
    options.api,
    options.path,
    options.verb,
    options.operation,
    options.config,
    options.clientId,
    options.clientSecret,
    options.languages,
    null,
    options.endpoint,
    options.values,
    options.apiHandler
  );

  const result = {
    path: options.path,
    type: options.type,
    method: options.verb,
    snippets,
    example: ''
  };
  return result;
}
